"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";

function parseSixtyFortyHeadline(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(proposons)/i);
    return parts.map((part, i) => {
      if (/proposons/i.test(part)) {
        return <span key={i} className="italic text-gold">{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }
  const parts = text.split(/(propose)/i);
  return parts.map((part, i) => {
    if (/propose/i.test(part)) {
      return <span key={i} className="italic text-gold">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function parseCaseStudyTitle(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(page de communiqués de presse)/i);
    return parts.map((part, i) => {
      if (/page de communiqués de presse/i.test(part)) {
        return <span key={i} className="line-through text-ink/40">{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }
  const parts = text.split(/(press release page)/i);
  return parts.map((part, i) => {
    if (/press release page/i.test(part)) {
      return <span key={i} className="line-through text-ink/40">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function parsePercentages(text: string) {
  return text.split(/(\d+\s*%)/).map((part, i) => {
    if (/100\s*%/.test(part)) {
      return <span key={i} className="italic text-gold">{part}</span>;
    }
    if (/40\s*%/.test(part)) {
      return <span key={i} className="font-bold text-gold">{part}</span>;
    }
    if (/60\s*%/.test(part)) {
      return <span key={i} className="font-bold">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export const SixtyForty = () => {
  const { t, lang } = useV2Translation();

  return (
    <section
      id="proof"
      data-testid="sixtyforty-section"
      className="py-24 lg:py-32 border-t border-line"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-16 lg:mb-20">
          <div className="col-span-12">
            <h2
              data-testid="sixtyforty-headline"
              className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight max-w-[26ch]"
            >
              {parseSixtyFortyHeadline(t.sixtyForty.headline, lang)}
            </h2>
            <p className="font-body text-lg lg:text-xl text-ink/70 mt-6 max-w-[58ch]">
              {t.sixtyForty.subtitle}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          {/* Left: The Logic */}
          <div
            className="col-span-12 lg:col-span-7"
            data-testid="sixtyforty-logic"
          >
            {/* Visual ratio bar — tilted 3D */}
            <motion.div
              initial={{ opacity: 0, rotateX: -22, y: 50 }}
              whileInView={{ opacity: 1, rotateX: 0, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              style={{ perspective: 1400, transformStyle: "preserve-3d" }}
              className="mb-10"
            >
              <motion.div
                whileHover={{ rotateY: 4, rotateX: -2, scale: 1.01 }}
                transition={{ type: "spring", stiffness: 140, damping: 18 }}
                style={{
                  transformStyle: "preserve-3d",
                  boxShadow: "12px 12px 0 0 #C9A84C",
                }}
                className="border border-navy"
              >
                <div className="grid grid-cols-10">
                  <div className="col-span-6 bg-navy text-paper p-6 lg:p-8 flex flex-col">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-paper/60">
                      {t.sixtyForty.visibleLabel}
                    </span>
                    <span className="font-display font-bold text-6xl lg:text-7xl leading-none mt-2">
                      {t.sixtyForty.visiblePct}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-widest text-paper/70 mt-2">
                      {t.sixtyForty.visibleDesc}
                    </span>
                  </div>
                  <div className="col-span-4 bg-gold text-navy-deep p-6 lg:p-8 flex flex-col diagonal-lines relative">
                    <div className="relative">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-navy-deep/80">
                        {t.sixtyForty.hiddenLabel}
                      </span>
                      <span className="font-display font-bold text-gold text-6xl lg:text-7xl leading-none mt-2 block">
                        {t.sixtyForty.hiddenPct}
                      </span>
                      <span className="font-mono text-xs uppercase tracking-widest text-navy-deep/85 mt-2 block">
                        {t.sixtyForty.hiddenDesc}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <div className="space-y-6 font-body text-lg leading-[1.7] text-ink/85 max-w-[58ch]">
              <p>{parsePercentages(t.sixtyForty.body1)}</p>
              <p>{parsePercentages(t.sixtyForty.body2)}</p>
              <p className="font-display text-2xl lg:text-3xl leading-snug tracking-tight text-ink">
                {parsePercentages(t.sixtyForty.body3)}
              </p>
              <p>{parsePercentages(t.sixtyForty.body4)}</p>
            </div>
          </div>

          {/* Right: Case study */}
          <motion.div
            className="col-span-12 lg:col-span-5"
            data-testid="sixtyforty-case"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 0.8,
              delay: 0.15,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
              className="border border-navy bg-paper-pure h-full p-8 lg:p-10 flex flex-col"
            >
              <h3 className="font-display text-3xl lg:text-4xl leading-tight tracking-tight mb-6">
                {parseCaseStudyTitle(t.sixtyForty.caseStudy.title, lang)}
              </h3>
              <p className="font-display italic text-2xl lg:text-3xl text-gold leading-snug mb-8">
                {t.sixtyForty.caseStudy.highlight}
              </p>

              <div className="space-y-4 font-body text-base text-ink/80 leading-relaxed flex-1">
                <p>{t.sixtyForty.caseStudy.body1}</p>
                <p>{t.sixtyForty.caseStudy.body2}</p>
                <p className="font-mono text-sm uppercase tracking-widest text-ink pt-2">
                  &rarr; {t.sixtyForty.caseStudy.ctaBody}
                </p>
              </div>

              <div className="mt-8 h-px bg-ink/20" />

              <a
                href="#booking"
                data-testid="sixtyforty-cta"
                className="hover-arrow mt-6 inline-flex items-center justify-between group w-full"
              >
                <span className="font-mono text-xs uppercase tracking-[0.18em]">
                  {t.sixtyForty.caseStudy.cta}
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-gold"
                  strokeWidth={2.2}
                />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
